import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { WORKSPACE_LAYOUT_BREAKPOINT_PX } from './workspace-layout';

type WorkspaceSidebarContextValue = {
  mobileOpen: boolean;
  isMobile: boolean;
  openMobile: () => void;
  closeMobile: () => void;
  toggleMobile: () => void;
};

const WorkspaceSidebarContext =
  createContext<WorkspaceSidebarContextValue | null>(null);

function useIsMobileSidebarViewport(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= WORKSPACE_LAYOUT_BREAKPOINT_PX;
  });

  useEffect(() => {
    const mq = window.matchMedia(
      `(max-width: ${WORKSPACE_LAYOUT_BREAKPOINT_PX}px)`,
    );
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

export function WorkspaceSidebarProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobileSidebarViewport();

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const openMobile = useCallback(() => setMobileOpen(true), []);
  const toggleMobile = useCallback(
    () => setMobileOpen(prev => !prev),
    [],
  );

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!mobileOpen || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen, isMobile]);

  const value = useMemo(
    () => ({
      mobileOpen,
      isMobile,
      openMobile,
      closeMobile,
      toggleMobile,
    }),
    [mobileOpen, isMobile, openMobile, closeMobile, toggleMobile],
  );

  return (
    <WorkspaceSidebarContext.Provider value={value}>
      {children}
    </WorkspaceSidebarContext.Provider>
  );
}

export function useWorkspaceSidebar() {
  const ctx = useContext(WorkspaceSidebarContext);
  return ctx;
}
